import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from "react-redux";
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import { createCustomHandleApostropheChange, removeTrailingSpacesAndDashes } from '../../../Components/CleanInputs';
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import CustomToast from '../../../Components/CustomToast'

export const SegmentCreate = ({
    visible,
    hidePopup,
    businessId,
    onSegmentsChanged,
    segmentFetchOptions,
}) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const [errors, setErrors] = useState({});
    const [err, setErr] = useState('');
    const [segmentName, setSegmentName] = useState('')
    const [toastTrigger, setToastTrigger] = useState(false);
    const [saveClicked, setSaveClicked] = useState(false);
    const [saveClickedError, setSaveClickedError] = useState(false);
    const [toastData, setToastData] = useState({
        summary: '',
        severity: '',
        detail: '',
        life: 0
    });

    //redux states
    const { creatingSegment, createdSegment, createSegmentError }
        = useSelector((state) => state.businessCustomers)

    //redux calls
    const createSegment = useCallback((segmentData) => dispatch(BusinessCustomersActions.createSegmentRequest(segmentData)), [dispatch]);
    const getSegment = useCallback(
        (id) =>
            dispatch(BusinessCustomersActions.fetchingSegmentRequest(id, segmentFetchOptions)),
        [dispatch, segmentFetchOptions]
    );
    const getSegmentCount = useCallback((businessId) => dispatch(BusinessCustomersActions.fetchingSegmentCountRequest(businessId)), [dispatch]);

    const showToast = (summary, severity, detail, life = 3000) => {
        setToastData({
            summary,
            severity,
            detail,
            life
        });
        setToastTrigger(true)
    }

    useEffect(() => {
        if (!creatingSegment && createSegmentError?.status === 400 && createSegmentError?.detail && saveClickedError) {
            setErr(createSegmentError.detail.split('"')[1]);
        } else if (!creatingSegment && createSegmentError && saveClickedError) {
            setErr(t("segment.failure_create"))
        } else setErr('')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createSegmentError, creatingSegment, saveClickedError])


    useEffect(() => {
        if (!creatingSegment && createdSegment && saveClicked) {
            showToast(t("success"), "success", t("segment.success_create"))
        }

        if (!creatingSegment && createSegmentError && saveClicked) {
            showToast(t("err"), "error", t("segment.failure_create"), 5000)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatingSegment, createdSegment, createSegmentError, saveClicked])


    const formik = useFormik({
        initialValues: {
            name: "",
        },
        validate: (data) => {
            let errors = {};
            if (!data.name) {
                errors.name = t("products.validate_error");
            }
            return errors;
        },
        onSubmit: (data) => {
            const segmentData = {
                name: removeTrailingSpacesAndDashes(data.name) || null,
                business: { id: businessId },
            };
            setSegmentName(data.name)
            setSaveClicked(true)
            createSegment(segmentData)
        },
    }
    );


    useEffect(() => {
        if (formik.values.name) {
            setSaveClickedError(false)
        }
    }, [formik.values.name])

    const commonParams = [formik, setErrors, t];

    const customHandleChange = createCustomHandleApostropheChange(...commonParams);

    const isFormFieldValid = (name) =>
        !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return (
            isFormFieldValid(name) && saveClicked && (
                <small className="p-error">{formik.errors[name]}</small>
            )
        );
    };

    return (
        <div>
            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    getSegment(businessId);
                    getSegmentCount(businessId);
                    onSegmentsChanged?.();
                    hidePopup()
                    setToastTrigger(false)
                    setSaveClicked(false);
                    setSaveClickedError(false)
                    setErr('')
                    setSegmentName('')
                    formik.setFieldValue("name", "");
                }}
                handleActionsAfterErrorToast={() => {
                    setToastTrigger(false)
                    setSaveClicked(false)
                    setSaveClickedError(true)
                }}
            />
            <Dialog
                visible={visible}
                onHide={() => {
                    hidePopup();
                    formik.setFieldValue('name', '');
                    setErr('')
                    setSaveClicked(false);
                    setSaveClickedError(false)
                }}
                header={t("business_customers.create_segment")}
                style={{ width: "350px" }}
            >
                <form onSubmit={formik.handleSubmit} className="p-fluid" style={{ paddingInline: "4px" }}>
                    <div className="p-field p-mb-3 mt-4" >
                        <label
                            id='name_label'
                            htmlFor="name"
                            className={classNames("product-form-label", {
                                "": isFormFieldValid("name")
                            })}
                        >
                            {t("common.name")}
                        </label>
                        <InputText
                            id="name"
                            name="name"
                            value={formik.values.name}
                            onChange={customHandleChange}
                            style={{ width: "100%" }}
                            maxLength={100}
                        />
                        {getFormErrorMessage("name")}
                        {err && <Trans i18nKey="segment.message_segment_name_exists"
                            values={{ segment: segmentName }}
                            components={{
                                1: <span className="tags_error_message" />,
                                2: <span className="tags_error_message_bold" />,
                                3: <span className="tags_error_message" />
                            }}
                            className='mt-1' />}
                        {errors.name ? <p className="character_errors">{errors.name}</p> : <p className='segment_hint'>{t("segment.segment_field_hint")}</p>}
                    </div>

                    <div className="p-field p-mb-3 mt-4" >
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                type="submit"
                                style={{ width: "auto", backgroundColor: "#d0021b" }}
                                label={t("products.save_product")}
                                loading={creatingSegment}
                                disabled={saveClicked || !formik.values.name || saveClickedError}
                                className="p-mt-2" />
                        </div>
                    </div>
                </form>
            </Dialog>

        </div >
    )
}


